import {ISolicitacaoTipoFormNovoDto, ISolicitacaoTipoNovaDto, tipoPrimitivo,} from "@models/solicitacoes/dto.ts";
import {MoneyInputEntry} from '@shared/specialComponents/InputEntry.tsx';
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {Add, Delete, DeleteSweepOutlined, Save, Warning} from "@mui/icons-material";
import {CriaSolicitacaoTipo, GetParametrosSolicitacoes} from "@localization/siteMap/endpoints/_SolicitacoesEnpoints.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {HideableComponent} from "@shared/specialComponents/HideableComponent.tsx";
import {NewForm} from "@components/solicitacoes/Form.tsx";
import {ISolicitacaoParametro} from "@models/solicitacoes/solicitacaoParametro.ts";
import {FormEntry} from "@components/solicitacoes/FormEntry.tsx";
import {ISolicitacaoTipo, ISolicitacaoTipoForm} from "@models/solicitacoes/solicitacoes.ts";

interface CreateSolicitacaoTipoProps {
    onCreate?: (tipo: ISolicitacaoTipo) => void;
}

/**
 * Component for creating a new solicitation type with parameters and form fields
 * @param props Component properties
 */
export function CreateSolicitacaoTipo(props: CreateSolicitacaoTipoProps) {
    type TabType = 'Parametros' | 'Campos';

    const {createAlertFromResponse, addPopUp} = useAlert();
    const scrollRef = useRef<HTMLDivElement | null>(null);

    // UI state
    const [currentTab, setCurrentTab] = useState<TabType>('Parametros');
    const [loading, setLoading] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [apiError, setApiError] = useState<string | null>(null);

    // Data state
    const [parameters, setParameters] = useState<ISolicitacaoParametro[]>([]);
    const [defaultParameters, setDefaultParameters] = useState<ISolicitacaoParametro[]>([]);
    const [formFields, setFormFields] = useState<ISolicitacaoTipoFormNovoDto[]>([]);

    const [creationData, setCreationData] = useState<ISolicitacaoTipoNovaDto>({
        nome: '',
        descricao: '',
        form: [],
        parametros: [],
    });

    // Derived state
    const distinctPrimitiveTypes = useMemo(() => {
        const types = parameters.map(param => param.tipoPrimitivo.toString());
        return [...new Set(types)] as tipoPrimitivo[];
    }, [parameters]);

    /**
     * Adds a new empty form field to the list
     */
    const addFormField = useCallback(() => {
        setFormFields(prevFields => [
            ...prevFields,
            {
                nome: '',
                descricao: '',
                tipoPrimitivo: 'String',
                obrigatorio: false,
                tipoSolicitacao: 0,
                ordem: prevFields.length,
            },
        ]);
    }, []);

    /**
     * Removes a form field at the specified index
     * @param index The index of the form field to remove
     */
    const removeFormField = useCallback((index: number) => {
        setFormFields(prevFields => {
            const newFields = [...prevFields];
            newFields.splice(index, 1);
            return newFields;
        });
    }, []);

    /**
     * Shows a confirmation dialog for removing fields with errors
     */
    const confirmRemoveFieldsWithErrors = useCallback(() => {
        addPopUp({
            title: "Remover campos com erro",
            content: (
                <Grid container spacing={2}>
                    <Grid size={{xs: 12}}>
                        <Typography>Deseja remover todos os campos com erro?</Typography>
                    </Grid>
                    <Grid>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={removeFieldsWithErrors}
                            startIcon={<DeleteSweepOutlined/>}
                        >
                            Remover
                        </Button>
                    </Grid>
                </Grid>
            )
        });
    }, [addPopUp]);

    /**
     * Removes all form fields that have validation errors
     */
    const removeFieldsWithErrors = useCallback(() => {
        setFormFields(prevFields =>
            prevFields.filter(field => field.nome.length >= 3)
        );
    }, []);

    /**
     * Submits the form to create a new solicitation type
     */
    const submitForm = useCallback(() => {
        // Create a copy of the data to avoid mutating state directly
        const data = {
            ...creationData,
            form: formFields.map((field, index) => ({
                ...field,
                ordem: index,
            }))
        };

        setLoading(true);
        setApiError(null);

        CriaSolicitacaoTipo(data)
            .then(response => {
                createAlertFromResponse(response);

                if (response.status === 'Success') {
                    // Reset form after successful submission
                    setCreationData({
                        nome: '',
                        descricao: '',
                        form: [],
                        parametros: defaultParameters,
                    });
                    setParameters(defaultParameters);
                    setFormFields([]);

                    // Call onCreate callback if provided
                    if (props.onCreate) {
                        props.onCreate(response.items);
                    }
                } else {
                    // Handle error response
                    setApiError(response.message || 'Erro ao criar solicitação');
                }
            })
            .catch(error => {
                console.error('Error creating solicitation type:', error);
                setApiError('Erro ao criar solicitação. Por favor, tente novamente.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [creationData, formFields, defaultParameters, createAlertFromResponse, props.onCreate]);

    /**
     * Updates a form field at the specified index
     * @param index The index of the form field to update
     * @param field The updated form field data
     */
    const updateFormField = useCallback((index: number, field: ISolicitacaoTipoFormNovoDto) => {
        setFormFields(prevFields => {
            const newFields = [...prevFields];
            newFields[index] = field;
            return newFields;
        });
    }, []);

    /**
     * Gets a list of duplicate field names
     * @returns Array of duplicate field names
     */
    const getDuplicateFieldNames = useCallback(() => {
        const names = formFields.map(field => field.nome);
        return names.filter((name, index) => names.indexOf(name) !== index);
    }, [formFields]);

    /**
     * Gets validation warnings for duplicate field names
     * @returns Array of warning messages for duplicate fields
     */
    const getDuplicateFieldWarnings = useCallback(() => {
        const names = formFields.map(field => field.nome);
        const duplicates = names.filter((name, index) => names.indexOf(name) !== index);

        return duplicates.map(name => `Campo '${name}' duplicado`);
    }, [formFields]);

    /**
     * Gets validation warnings for fields with names that are too short
     * @returns Array of warning messages for fields with short names
     */
    const getShortFieldNameWarnings = useCallback(() => {
        return formFields
            .filter(field => field.nome.length > 0 && field.nome.length < 3)
            .map(field => `Campo '${field.nome}' tem menos de 3 caracteres`);
    }, [formFields]);

    /**
     * Shows a popup with all current warnings
     */
    const showWarnings = useCallback(() => {
        addPopUp({
            title: "Avisos",
            content: (
                <Grid container spacing={1}>
                    {warnings.map((warning, index) => (
                        <Grid size={{xs: 12}} key={index}>
                            <Typography
                                variant="body2"
                                color="error"
                                // startIcon={<Warning fontSize="small" />}
                            >
                                {warning}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            )
        });
    }, [warnings, addPopUp]);

    /**
     * Updates a parameter value
     * @param parameter The parameter to update
     * @param value The new value for the parameter
     */
    const handleParameterChange = useCallback((parameter: ISolicitacaoParametro, value: string) => {
        setCreationData(prevData => {
            // Check if the parameter already exists in the list
            if (prevData.parametros && prevData.parametros.some(p => p.id === parameter.id)) {
                // Update existing parameter
                return {
                    ...prevData,
                    parametros: prevData.parametros.map(p =>
                        p.id === parameter.id ? {...p, valor: value} : p
                    )
                };
            } else {
                // Add new parameter
                return {
                    ...prevData,
                    parametros: [
                        ...prevData.parametros,
                        {
                            id: parameter.id,
                            valor: value,
                        },
                    ],
                };
            }
        });
    }, []);

    // Update warnings when form fields change
    useEffect(() => {
        const warnings = [
            ...getShortFieldNameWarnings(),
            ...getDuplicateFieldWarnings()
        ];
        setWarnings(warnings);
    }, [formFields, getShortFieldNameWarnings, getDuplicateFieldWarnings]);

    // Load parameters on component mount
    useEffect(() => {
        const fetchParameters = async () => {
            try {
                setLoading(true);
                const response = await GetParametrosSolicitacoes({page: 1, pageSize: 100});

                setParameters(response.items);
                setDefaultParameters(response.items);

                // Set default parameter values
                const defaultParams = response.items
                    .filter(param => param.valorPadrao)
                    .map(param => ({
                        id: param.id,
                        valor: param.valorPadrao as string
                    }));

                setCreationData(prevData => ({
                    ...prevData,
                    parametros: defaultParams,
                }));
            } catch (error) {
                console.error('Error fetching parameters:', error);
                setApiError('Erro ao carregar parâmetros. Por favor, recarregue a página.');
            } finally {
                setLoading(false);
            }
        };

        fetchParameters();

        // No cleanup needed as we want to load parameters only once
    }, []);

    /**
     * Memoized component for rendering parameters grouped by type
     */
    const ParametersComponent = useMemo(() => {
        return (
            <Grid container spacing={2}>
                {distinctPrimitiveTypes.map(type => (
                    <Grid size={{xs: 12}} key={type}>
                        <Typography variant="subtitle1" gutterBottom>
                            {type === 'Money' ? 'Valores Monetários' :
                                type === 'String' ? 'Textos' :
                                    type === 'Bool' ? 'Opções Sim/Não' :
                                        type === 'Date' ? 'Datas' : type}
                        </Typography>
                        <Divider sx={{mb: 2}}/>
                        <Grid container spacing={2}>
                            {parameters
                                .filter(param => param.tipoPrimitivo === type)
                                .map((param, index) => (
                                    <Grid size={{xs: 12, md: 6}} key={param.id || index}>
                                        {param.tipoPrimitivo === 'Money' ? (
                                            <MoneyInputEntry
                                                label={param.nome}
                                                tooltip={param.descricao}
                                                value={Number(
                                                    creationData.parametros?.find(p => p.id === param.id)?.valor ??
                                                    param.valorPadrao
                                                )}
                                                onChange={value => {
                                                    handleParameterChange(param, value?.toString() ?? '');
                                                }}
                                            />
                                        ) : (
                                            <FormEntry
                                                captureValue={value => {
                                                    handleParameterChange(param, value.toString());
                                                }}
                                                defaultValue={
                                                    creationData.parametros?.find(p => p.id === param.id)?.valor ??
                                                    param.valorPadrao
                                                }
                                                form={param as unknown as ISolicitacaoTipoForm}
                                            />
                                        )}
                                    </Grid>
                                ))
                            }
                        </Grid>
                    </Grid>
                ))}
            </Grid>
        );
    }, [distinctPrimitiveTypes, parameters, creationData.parametros, handleParameterChange]);

    /**
     * Memoized component for rendering the header form fields
     */
    const HeaderComponent = useMemo(() => {
        return (
            <Grid container spacing={2}>
                <Grid size={{xs: 12}}>
                    <TextField
                        label="Nome do formulário"
                        error={creationData.nome.length > 0 && creationData.nome.length < 3}
                        helperText={creationData.nome.length > 0 && creationData.nome.length < 3 ?
                            "Nome deve ter pelo menos 3 caracteres" : ""}
                        value={creationData.nome}
                        size="small"
                        fullWidth
                        onChange={(e) => {
                            setCreationData(prev => ({
                                ...prev,
                                nome: e.target.value,
                            }));
                        }}
                        placeholder="Nome do formulário"
                        required
                    />
                </Grid>
                <Grid size={{xs: 12}}>
                    <TextField
                        label="Descrição do formulário"
                        value={creationData.descricao}
                        fullWidth
                        multiline
                        rows={2}
                        onChange={(e) => {
                            setCreationData(prev => ({
                                ...prev,
                                descricao: e.target.value,
                            }));
                        }}
                        placeholder="Descrição do formulário"
                    />
                </Grid>
            </Grid>
        );
    }, [creationData.nome, creationData.descricao])


    /**
     * Memoized component for rendering form fields
     */
    const FormFieldsComponent = useMemo(() => {
        // Only calculate duplicate names once per render
        const duplicateNames = getDuplicateFieldNames();

        return (
            <Grid container spacing={2}>
                <Grid size={{xs: 12}}>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                        <Grid>
                            <Button
                                onClick={showWarnings}
                                variant="outlined"
                                startIcon={<Warning/>}
                                disabled={warnings.length === 0}
                                aria-label="Mostrar avisos"
                            >
                                Avisos ({warnings.length})
                            </Button>
                        </Grid>
                        <Grid>
                            <ButtonGroup>
                                <Tooltip title="Remover todos os campos">
                                    <Button
                                        startIcon={<Delete/>}
                                        onClick={() => setFormFields([])}
                                        disabled={formFields.length === 0}
                                        variant="outlined"
                                        color="error"
                                    >
                                        Remover todos
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Remover campos com erro">
                                    <Button
                                        onClick={confirmRemoveFieldsWithErrors}
                                        disabled={warnings.length === 0}
                                        variant="outlined"
                                        color="error"
                                        aria-label="Remover campos com erro"
                                    >
                                        <DeleteSweepOutlined/>
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Adicionar novo campo">
                                    <Button
                                        startIcon={<Add/>}
                                        onClick={addFormField}
                                        variant="outlined"
                                        color="primary"
                                        aria-label="Adicionar campo"
                                    >
                                        Adicionar campo
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{xs: 12}}>
                    <Paper
                        elevation={1}
                        sx={{
                            maxHeight: '45vh',
                            overflow: 'auto',
                            borderRadius: 1,
                            p: 2
                        }}
                        ref={scrollRef}
                    >
                        {formFields.length === 0 ? (
                            <Typography variant="body1" align="center" color="textSecondary" sx={{py: 4}}>
                                Nenhum campo adicionado. Clique em "Adicionar campo" para começar.
                            </Typography>
                        ) : (
                            formFields.map((field, index) => {
                                // Only render fields that are visible in the viewport
                                // This addresses the TODO comment about slowness
                                return (
                                    <HideableComponent
                                        key={`field-${index}`}
                                        isVisibleCallback={() => true}
                                    >
                                        <NewForm
                                            id={index}
                                            form={field}
                                            mount={true}
                                            hasError={duplicateNames.some(
                                                name => name.toUpperCase() === field.nome.toUpperCase()
                                            )}
                                            onChange={(updatedField) => {
                                                updateFormField(index, updatedField);
                                            }}
                                            remove={() => removeFormField(index)}
                                        />
                                    </HideableComponent>
                                );
                            })
                        )}
                    </Paper>
                </Grid>
            </Grid>
        );
    }, [
        formFields,
        warnings.length,
        getDuplicateFieldNames,
        showWarnings,
        confirmRemoveFieldsWithErrors,
        addFormField,
        updateFormField,
        removeFormField
    ]);

    return (
        <Grid container spacing={3}>
            {/* API Error message */}
            {apiError && (
                <Grid size={{xs: 12}}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: 'error.light',
                            color: 'error.contrastText',
                            borderRadius: 1
                        }}
                    >
                        <Typography variant="body2">{apiError}</Typography>
                    </Paper>
                </Grid>
            )}

            {/* Save button */}
            <Grid size={{xs: 12}}>
                <Grid container justifyContent="flex-end">
                    <Button
                        endIcon={loading ? <CircularProgress size={20}/> : <Save/>}
                        disabled={loading || creationData.nome.length < 3}
                        variant="contained"
                        onClick={submitForm}
                        color="success"
                        aria-label="Salvar formulário">
                        {loading ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Grid>
            </Grid>

            {/* Header form fields */}
            <Grid size={{xs: 12}}>
                {HeaderComponent}
            </Grid>

            {/* Tabs */}
            <Grid size={{xs: 12}}>
                <Tabs
                    value={currentTab}
                    variant="standard"
                    onChange={(_event, value: TabType) => {
                        setCurrentTab(value);
                    }}
                    aria-label="Abas de configuração"
                >
                    <Tab label="Parâmetros" value="Parametros"/>
                    <Tab label="Campos" value="Campos"/>
                </Tabs>

                {/* Tab content */}
                <Box sx={{mt: 2}}>
                    {loading && (
                        <Grid container justifyContent="center" sx={{py: 4}}>
                            <CircularProgress/>
                        </Grid>
                    )}

                    {!loading && currentTab === 'Parametros' && ParametersComponent}
                    {!loading && currentTab === 'Campos' && FormFieldsComponent}
                </Box>
            </Grid>
        </Grid>
    );
}